import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { firestore } from '@/firebase';

/**
 * @param {object|null|undefined} supply
 * @returns {boolean}
 */
export function isSupplyArchived(supply) {
  return !!supply?.archived;
}

/**
 * @param {string} supplyId
 * @param {Record<string, object>} servicesMap
 * @returns {Array<{ id: string, name: string }>}
 */
export function findServicesLinkedToSupply(supplyId, servicesMap) {
  const linked = [];
  for (const service of Object.values(servicesMap || {})) {
    const cfg = service.supplyConfig;
    if (!cfg) continue;
    let found = false;
    for (const row of cfg.quantity || []) {
      if (row?.supplyId === supplyId) {
        found = true;
        break;
      }
    }
    if (!found) {
      for (const group of cfg.containers || []) {
        const ids = Array.isArray(group.allowedSupplyIds) ? group.allowedSupplyIds : [];
        if (ids.includes(supplyId)) {
          found = true;
          break;
        }
      }
    }
    if (found) {
      linked.push({ id: service.id, name: service.name || service.id });
    }
  }
  linked.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  return linked;
}

/**
 * Remove a supply from a service supplyConfig (quantity rows and container allowedSupplyIds).
 * @param {object|null|undefined} supplyConfig
 * @param {string} supplyId
 * @returns {{ quantity: Array, containers: Array }}
 */
export function stripSupplyFromSupplyConfig(supplyConfig, supplyId) {
  const cfg = supplyConfig || {};
  const quantity = (cfg.quantity || [])
    .filter((row) => row?.supplyId !== supplyId)
    .map((row) => ({
      supplyId: row.supplyId,
      defaultQuantity: Number(row.defaultQuantity) || 0,
    }));

  const containers = (cfg.containers || [])
    .map((group) => ({
      groupId: group.groupId,
      groupLabel: group.groupLabel || '',
      allowedSupplyIds: (group.allowedSupplyIds || []).filter((id) => id !== supplyId),
      ...(Array.isArray(group.allowedCategories) && group.allowedCategories.length > 0
        ? { allowedCategories: [...group.allowedCategories] }
        : {}),
    }))
    .filter((group) => {
      const ids = group.allowedSupplyIds || [];
      const cats = group.allowedCategories || [];
      return ids.length > 0 || cats.length > 0;
    });

  return { quantity, containers };
}

/**
 * @param {string} supplyId
 * @param {Record<string, object>} servicesMap
 * @returns {Array<{ serviceId: string, supplyConfig: object }>}
 */
export function buildServiceUpdatesUnlinkingSupply(supplyId, servicesMap) {
  const updates = [];
  for (const service of Object.values(servicesMap || {})) {
    if (!service?.id) continue;
    const cfg = service.supplyConfig;
    if (!cfg) continue;

    let linked = false;
    for (const row of cfg.quantity || []) {
      if (row?.supplyId === supplyId) {
        linked = true;
        break;
      }
    }
    if (!linked) {
      for (const group of cfg.containers || []) {
        const ids = Array.isArray(group.allowedSupplyIds) ? group.allowedSupplyIds : [];
        if (ids.includes(supplyId)) {
          linked = true;
          break;
        }
      }
    }
    if (!linked) continue;

    updates.push({
      serviceId: service.id,
      supplyConfig: stripSupplyFromSupplyConfig(cfg, supplyId),
    });
  }
  return updates;
}

/**
 * @param {string} supplyId
 * @returns {Promise<boolean>}
 */
export async function supplyHasInventoryTransactions(supplyId) {
  const txQuery = query(
    collection(firestore, 'inventoryTransactions'),
    where('supplyId', '==', supplyId),
    limit(1),
  );
  const snap = await getDocs(txQuery);
  return !snap.empty;
}

/**
 * Active or depleted open-container docs for this supply.
 * @param {string} supplyId
 * @returns {Promise<boolean>}
 */
export async function supplyHasOpenContainerDocs(supplyId) {
  const containerQuery = query(
    collection(firestore, 'openContainers'),
    where('supplyId', '==', supplyId),
    limit(1),
  );
  const snap = await getDocs(containerQuery);
  return !snap.empty;
}

/**
 * @param {string} supplyId
 * @param {Record<string, object>} servicesMap
 * @param {number} activeOpenCount
 * @returns {Promise<object>}
 */
export async function assessSupplyDeletion(supplyId, servicesMap, activeOpenCount) {
  if (activeOpenCount > 0) {
    return { action: 'block', activeOpenCount };
  }

  const linkedServices = findServicesLinkedToSupply(supplyId, servicesMap);
  const [hasTransactions, hasContainers] = await Promise.all([
    supplyHasInventoryTransactions(supplyId),
    supplyHasOpenContainerDocs(supplyId),
  ]);

  if (hasTransactions || hasContainers || linkedServices.length > 0) {
    return {
      action: 'archive',
      linkedServices,
      hasTransactions,
      hasContainers,
    };
  }

  return { action: 'hard_delete' };
}

/**
 * @param {string} supplyName
 * @param {object} assessment
 * @returns {string}
 */
export function formatArchiveDeleteMessage(supplyName, assessment) {
  const parts = [];
  let unlinkNote = '';

  if (assessment.linkedServices?.length) {
    const names = assessment.linkedServices.map((s) => s.name).join(', ');
    const n = assessment.linkedServices.length;
    parts.push(
      n === 1
        ? `vinculado a 1 serviço (${names})`
        : `vinculado a ${n} serviços (${names})`,
    );
    unlinkNote = n === 1
      ? ' O vínculo com esse serviço será removido.'
      : ` Os vínculos com esses ${n} serviços serão removidos.`;
  }
  if (assessment.hasTransactions) {
    parts.push('com movimentações no estoque');
  }
  if (assessment.hasContainers) {
    parts.push('com histórico de bandejas/frascos abertos');
  }
  const detail = parts.length ? ` Este insumo está ${parts.join(' e ')}.` : '';
  return `"${supplyName}" já foi usado.${detail}${unlinkNote} Será arquivado e removido da lista, mas o histórico será mantido. Deseja continuar?`;
}

/**
 * @param {string} supplyName
 * @returns {string}
 */
export function formatHardDeleteMessage(supplyName) {
  return `Excluir "${supplyName}" permanentemente? Esta ação não pode ser desfeita.`;
}

/**
 * @param {object|null|undefined} supply
 * @param {number} activeOpenCount
 * @returns {string}
 */
export function formatBlockOpenContainersMessage(supply, activeOpenCount) {
  const unit = activeOpenCount === 1
    ? (supply?.unit || 'unidade')
    : (supply?.unitPlural || supply?.unit || 'unidades');
  return `Há ${activeOpenCount} ${unit} aberto(a)(s). Esgote antes de excluir ou arquivar.`;
}
